import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Table } from "reactstrap";

import { adminUserBilling } from "../../../api/api.service";
import { describeAccess, formatDate, formatMoney } from "./insightLabels";

const STATUS_TONE = {
  SUCCEEDED: "good",
  REFUNDED: "neutral",
  PENDING: "warn",
  PENDING_CONFIRMATION: "warn",
  FAILED: "bad",
  EXPIRED: "bad",
};

const LICENSE_TONE = {
  ACTIVE: "good",
  PENDING: "warn",
  GRACE_PERIOD: "warn",
  EXPIRED: "bad",
  CANCELLED: "neutral",
};

const SCHOOL_ADMIN_STATUS = {
  PENDING: "Waiting for approval",
  APPROVED: "Approved",
  DECLINED: "Declined",
  REVOKED: "Revoked",
};

const titleCase = (value) =>
  value
    ? String(value)
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase())
    : "—";

function Row({ label, children }) {
  return (
    <div className="cd-kv">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * Where this person's access comes from and what they have paid — read
 * through the teacher's own billing service, so it matches their billing page.
 */
export default function PlanPayments() {
  const { id } = useParams();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    adminUserBilling(id)
      .then((res) => {
        if (!cancelled) setBilling(res?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (failed)
    return (
      <p className="cd-empty">Could not load billing. Try again in a moment.</p>
    );
  if (!billing) return loading ? <p className="cd-empty">Loading…</p> : null;

  const access = describeAccess(billing.access);
  const sub = billing.subscription ?? {};
  const card = sub.card;

  return (
    <div className="cd-plan">
      <div className="cd-grid">
        <section className="cd-panel">
          <header className="cd-panel__head">
            <h3>Access</h3>
          </header>
          <div
            className={`cd-access cd-access--${access.tone} cd-access--block`}
          >
            <span className="cd-access__label">{access.label}</span>
            {access.detail ? (
              <span className="cd-access__detail">{access.detail}</span>
            ) : null}
          </div>
          <dl className="cd-kvs">
            <Row label="Trial">
              {billing.access.trialStartedAt
                ? `${formatDate(billing.access.trialStartedAt)} → ${formatDate(billing.access.trialEndsAt)}`
                : "Never started"}
            </Row>
            {billing.access.grandfatheredAt ? (
              <Row label="Grandfathered">
                {formatDate(billing.access.grandfatheredAt)}
              </Row>
            ) : null}
            <Row label="School seat">
              {billing.schoolSeats?.length
                ? `${billing.schoolSeats[0].school || "School"} · ${titleCase(billing.schoolSeats[0].tier)}`
                : "None"}
            </Row>
          </dl>
        </section>

        <section className="cd-panel">
          <header className="cd-panel__head">
            <h3>Individual subscription</h3>
          </header>
          {sub.status ? (
            <dl className="cd-kvs">
              <Row label="Status">{titleCase(sub.status)}</Row>
              {billing.currentPlan ? (
                <Row label="Plan">
                  {billing.currentPlan.interval
                    ? `${titleCase(billing.currentPlan.interval)} · `
                    : ""}
                  {formatMoney(
                    billing.currentPlan.amountCents,
                    billing.currentPlan.currency,
                  )}
                </Row>
              ) : null}
              <Row label={sub.autoRenew ? "Renews on" : "Ends on"}>
                {formatDate(sub.renewsOn)}
              </Row>
              <Row label="Auto-renew">{sub.autoRenew ? "On" : "Off"}</Row>
              {sub.gracePeriodEndsAt ? (
                <Row label="Grace period ends">
                  {formatDate(sub.gracePeriodEndsAt)}
                </Row>
              ) : null}
              {sub.pendingPlan ? (
                <Row label="Switching to">
                  {sub.pendingPlan.name} ·{" "}
                  {formatMoney(
                    sub.pendingPlan.priceCents,
                    sub.pendingPlan.currency,
                  )}
                </Row>
              ) : null}
              <Row label="Card on file">
                {card ? (
                  <span className="cd-card">
                    <strong>{card.brand}</strong> •••• {card.last4}
                    <small>
                      exp {String(card.expMonth).padStart(2, "0")}/
                      {String(card.expYear).slice(-2)}
                    </small>
                  </span>
                ) : (
                  "None saved"
                )}
              </Row>
            </dl>
          ) : (
            <p className="cd-empty">No individual subscription.</p>
          )}
        </section>

        {billing.schoolAdmin || billing.schoolLicenses?.length ? (
          <section className="cd-panel cd-grid__wide">
            <header className="cd-panel__head">
              <h3>School administrator</h3>
              <p>
                {billing.schoolAdmin
                  ? `${SCHOOL_ADMIN_STATUS[billing.schoolAdmin.status] || billing.schoolAdmin.status}${
                      billing.schoolAdmin.school
                        ? ` for ${billing.schoolAdmin.school}`
                        : ""
                    }`
                  : null}
              </p>
            </header>
            {billing.schoolAdmin?.declineReason ? (
              <p className="text-muted small mb-2">
                Declined: {billing.schoolAdmin.declineReason}
              </p>
            ) : null}
            {billing.schoolLicenses?.length ? (
              <Table responsive className="cd-table mb-0">
                <thead>
                  <tr>
                    <th>Licence</th>
                    <th>Status</th>
                    <th>Teachers joined</th>
                    <th>Runs to</th>
                    <th>Last payment</th>
                    <th>Bought by</th>
                  </tr>
                </thead>
                <tbody>
                  {billing.schoolLicenses.map((license) => (
                    <tr key={license.id}>
                      <td>
                        {titleCase(license.tier)} ·{" "}
                        {titleCase(license.billingInterval)}
                        <small className="d-block text-muted">
                          #{license.id} · created{" "}
                          {formatDate(license.createdAt)}
                        </small>
                      </td>
                      <td>
                        <span
                          className={`cd-pill cd-pill--${LICENSE_TONE[license.status] || "neutral"}`}
                        >
                          {titleCase(license.status)}
                        </span>
                      </td>
                      <td>
                        {license.seatsClaimed}
                        {license.seatsTotal != null
                          ? ` of ${license.seatsTotal}`
                          : ""}
                      </td>
                      <td>{formatDate(license.currentPeriodEnd)}</td>
                      <td>
                        {license.lastPayment ? (
                          <>
                            <span
                              className={`cd-pill cd-pill--${STATUS_TONE[license.lastPayment.status] || "neutral"}`}
                            >
                              {titleCase(license.lastPayment.status)}
                            </span>{" "}
                            {formatMoney(
                              license.lastPayment.totalCents,
                              license.lastPayment.currency,
                            )}
                          </>
                        ) : (
                          "No payment made"
                        )}
                      </td>
                      <td>
                        {license.boughtByThisUser
                          ? "This person"
                          : "Another admin"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="cd-empty">No licence bought for this school yet.</p>
            )}
          </section>
        ) : null}

        {billing.schoolSeats?.length ? (
          <section className="cd-panel cd-grid__wide">
            <header className="cd-panel__head">
              <h3>School seats</h3>
            </header>
            {billing.schoolSeats?.length ? (
              <Table responsive className="cd-table mb-0">
                <thead>
                  <tr>
                    <th>School</th>
                    <th>Plan</th>
                    <th>Seat</th>
                    <th>Licence</th>
                    <th>Runs to</th>
                  </tr>
                </thead>
                <tbody>
                  {billing.schoolSeats.map((seat, i) => (
                    <tr key={`${seat.school}-${i}`}>
                      <td>{seat.school || "—"}</td>
                      <td>
                        {titleCase(seat.tier)} ·{" "}
                        {titleCase(seat.billingInterval)}
                      </td>
                      <td>
                        {seat.status === "CLAIMED"
                          ? `Joined ${formatDate(seat.claimedAt)}`
                          : `Invited ${formatDate(seat.invitedAt)}`}
                      </td>
                      <td>{titleCase(seat.licenseStatus)}</td>
                      <td>{formatDate(seat.currentPeriodEnd)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
          </section>
        ) : null}

        <section className="cd-panel cd-grid__wide">
          <header className="cd-panel__head">
            <h3>Payments</h3>
            <p>
              {billing.payments?.length
                ? `${billing.payments.length} in total`
                : null}
            </p>
          </header>
          {billing.payments?.length ? (
            <Table responsive className="cd-table mb-0">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Period</th>
                  <th className="text-end">Amount</th>
                  <th>Status</th>
                  <th>Paid</th>
                </tr>
              </thead>
              <tbody>
                {billing.payments.map((p) => (
                  <tr key={p.id}>
                    <td>{p.invoiceNumber || `#${p.id}`}</td>
                    <td className="text-nowrap">
                      {formatDate(p.periodStart)} → {formatDate(p.periodEnd)}
                    </td>
                    <td className="text-end">
                      {formatMoney(p.totalCents, p.currency)}
                    </td>
                    <td>
                      <span
                        className={`cd-pill cd-pill--${STATUS_TONE[p.status] || "neutral"}`}
                      >
                        {titleCase(p.status)}
                      </span>
                      {p.refundsPaymentId ? (
                        <small className="d-block text-muted">
                          Refund of #{p.refundsPaymentId}
                        </small>
                      ) : null}
                      {p.failureReason ? (
                        <small className="d-block text-muted">
                          {p.failureReason}
                        </small>
                      ) : null}
                    </td>
                    <td>{formatDate(p.paidAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="cd-empty">No payments yet.</p>
          )}
        </section>
      </div>
    </div>
  );
}
